// 탭·검색 파라미터 화이트리스트/normalize 단일 모듈 (구 content-tabs.ts + admin-route-search.ts 통합)

// ── 공통: 기간 옵션 (구 ANALYSIS_DAY_OPTIONS / RESUME_DAY_OPTIONS 통합) ──
export const DAY_OPTIONS = [7, 30, 100, 365] as const;
export type DayOption = (typeof DAY_OPTIONS)[number];

export function normalizeDays(value: unknown): DayOption {
  const days = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return DAY_OPTIONS.includes(days as DayOption) ? days as DayOption : 30;
}

// ── 콘텐츠 탭 ──
export const CONTENT_TABS = [
  { value: 'posts', label: '글' },
  { value: 'portfolio', label: '포트폴리오' },
  { value: 'resume', label: '이력서' },
] as const;
export type ContentTab = (typeof CONTENT_TABS)[number]['value'];

export const POSTS_FILTERS = ['all', 'published', 'draft'] as const;
export type PostsFilter = (typeof POSTS_FILTERS)[number];

export type ContentSearch = {
  tab: ContentTab;
  status: PostsFilter;
  q: string;
  days: DayOption;
};

const CONTENT_TAB_VALUES = CONTENT_TABS.map(tab => tab.value);

export function normalizeContentTab(value: unknown): ContentTab {
  return typeof value === 'string' && CONTENT_TAB_VALUES.includes(value as ContentTab)
    ? value as ContentTab
    : 'posts';
}

export function normalizePostsFilter(value: unknown): PostsFilter {
  return typeof value === 'string' && POSTS_FILTERS.includes(value as PostsFilter)
    ? value as PostsFilter
    : 'all';
}

export function normalizeContentSearch(search: Record<string, unknown>): ContentSearch {
  return {
    tab: normalizeContentTab(search.tab),
    status: normalizePostsFilter(search.status),
    q: typeof search.q === 'string' ? search.q.trim() : '',
    days: normalizeDays(search.days),
  };
}

// ── 분석 탭 ──
export const ANALYSIS_TABS = ['traffic', 'search', 'seo'] as const;
export type AnalysisTab = (typeof ANALYSIS_TABS)[number];

export const ANALYSIS_SEO_FILTERS = ['all', 'needs'] as const;
export type AnalysisSeoFilter = (typeof ANALYSIS_SEO_FILTERS)[number];

export type AnalysisSearch = {
  tab: AnalysisTab;
  trafficDays: DayOption;
  searchDays: DayOption;
  seoFilter: AnalysisSeoFilter;
};

export function normalizeAnalysisTab(value: unknown): AnalysisTab {
  return typeof value === 'string' && ANALYSIS_TABS.includes(value as AnalysisTab)
    ? value as AnalysisTab
    : 'traffic';
}

export function normalizeAnalysisSeoFilter(value: unknown): AnalysisSeoFilter {
  return typeof value === 'string' && ANALYSIS_SEO_FILTERS.includes(value as AnalysisSeoFilter)
    ? value as AnalysisSeoFilter
    : 'all';
}

export function normalizeAnalysisSearch(search: Record<string, unknown>): AnalysisSearch {
  return {
    tab: normalizeAnalysisTab(search.tab),
    trafficDays: normalizeDays(search.trafficDays),
    searchDays: normalizeDays(search.searchDays),
    seoFilter: normalizeAnalysisSeoFilter(search.seoFilter),
  };
}

// ── 챗 탭 ──
export const CHAT_TABS = ['conversations', 'article-qa', 'stats'] as const;
export type ChatTab = (typeof CHAT_TABS)[number];

export type ChatSearch = {
  tab: ChatTab;
};

export function normalizeChatTab(value: unknown): ChatTab {
  return typeof value === 'string' && CHAT_TABS.includes(value as ChatTab)
    ? value as ChatTab
    : 'conversations';
}

export function normalizeChatSearch(search: Record<string, unknown>): ChatSearch {
  return { tab: normalizeChatTab(search.tab) };
}

// ── 메시지 ──
export const MESSAGES_FILTERS = ['all', 'unread', 'read'] as const;
export type MessagesFilter = (typeof MESSAGES_FILTERS)[number];

export type MessagesSearch = {
  filter: MessagesFilter;
  page: number;
};

export function normalizeMessagesFilter(value: unknown): MessagesFilter {
  return typeof value === 'string' && MESSAGES_FILTERS.includes(value as MessagesFilter)
    ? value as MessagesFilter
    : 'all';
}

export function normalizeMessagesPage(value: unknown): number {
  const page = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isInteger(page) && page > 0 ? page : 1;
}

export function normalizeMessagesSearch(search: Record<string, unknown>): MessagesSearch {
  return {
    filter: normalizeMessagesFilter(search.filter),
    page: normalizeMessagesPage(search.page),
  };
}
